import { Badge } from '@payaca/ambrosia';
import { type FC, useMemo } from 'react';

import useGetCustomer from '@/api/queries/customers/useGetCustomer';
import useGetPipeline from '@/api/queries/pipelines/useGetPipeline';
import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import { useGetProposal } from '@/api/queries/proposal/useGetProposal';
import useGetSupplier from '@/api/queries/suppliers/useGetSupplier';

import {
  type PageContextBadge,
  parsePageContextBadges,
} from './pageContextBadgeMap';

/**
 * The badge chip itself. Renders nothing until a display value resolves, so a
 * badge only appears once we have something relevant to show for the entity.
 */
const ContextBadge: FC<{ label: string; value?: string | null }> = (props) => {
  const { label, value } = props;

  if (value == null || value === '') return null;

  return (
    <Badge variant="soft" colour="gray" size="sm" rounded>
      {`${label}: ${value}`}
    </Badge>
  );
};

const reference = (value?: string | number | null): string | undefined =>
  value == null ? undefined : `#${value}`;

/**
 * Resolvers fetch the entity's display value from the same cached query the
 * page already populated, so no extra request is made on entity pages.
 */
const ProjectContextBadge: FC<{ badge: PageContextBadge }> = (props) => {
  const { badge } = props;
  const { project } = useGetProjectByIdOverview(badge.id);
  return (
    <ContextBadge label={badge.label} value={reference(project?.reference)} />
  );
};

const ProposalContextBadge: FC<{ badge: PageContextBadge }> = (props) => {
  const { badge } = props;
  const { proposal } = useGetProposal(badge.id);
  return (
    <ContextBadge
      label={badge.label}
      value={reference(proposal?.customReference ?? proposal?.reference)}
    />
  );
};

const EstimateContextBadge: FC<{ badge: PageContextBadge }> = (props) => {
  const { badge } = props;
  const { estimate } = useGetProjectEstimate(badge.parentProjectId, badge.id);
  return (
    <ContextBadge label={badge.label} value={reference(estimate?.reference)} />
  );
};

const CustomerContextBadge: FC<{ badge: PageContextBadge }> = (props) => {
  const { badge } = props;
  const { customer } = useGetCustomer(badge.id);
  return <ContextBadge label={badge.label} value={customer?.name} />;
};

const SupplierContextBadge: FC<{ badge: PageContextBadge }> = (props) => {
  const { badge } = props;
  const { supplier } = useGetSupplier(badge.id);
  return <ContextBadge label={badge.label} value={supplier?.name} />;
};

const PipelineContextBadge: FC<{ badge: PageContextBadge }> = (props) => {
  const { badge } = props;
  const { pipeline } = useGetPipeline(badge.id);
  return <ContextBadge label={badge.label} value={pipeline?.title} />;
};

/** Dispatches to the resolver that fetches a display value for the entity. */
const EntityBadge: FC<{ badge: PageContextBadge }> = (props) => {
  const { badge } = props;

  switch (badge.type) {
    case 'project':
      return <ProjectContextBadge badge={badge} />;
    case 'proposal':
      return <ProposalContextBadge badge={badge} />;
    case 'estimate':
      return <EstimateContextBadge badge={badge} />;
    case 'customer':
      return <CustomerContextBadge badge={badge} />;
    case 'supplier':
      return <SupplierContextBadge badge={badge} />;
    case 'pipeline':
      return <PipelineContextBadge badge={badge} />;
  }
};

/**
 * Renders a Badge for each entity implied by the current page URL, so the user
 * can see at a glance which entities Dave has in context. Multiple badges
 * appear for nested pages (e.g. a proposal within a project). A badge is only
 * shown once its display value has been retrieved.
 */
export const PageContextBadges: FC<{ pathname: string }> = (props) => {
  const { pathname } = props;
  const badges = useMemo(() => parsePageContextBadges(pathname), [pathname]);

  if (badges.length === 0) return null;

  return (
    <div
      className="mb-2 flex flex-wrap items-center gap-1.5 empty:hidden"
      aria-label="Page context"
    >
      {badges.map((badge) => (
        <EntityBadge key={`${badge.type}:${badge.id}`} badge={badge} />
      ))}
    </div>
  );
};
